import { Button, Icon, Progress, Select, Step, Switch, Table, Upload } from "@alifd/next";
import { BefGlobalContext, BefGuid, BefMessageServiceContext, BefRpcContext } from "@beflam/bef-core";
import { createContext, memo, useContext, useEffect, useState } from "react";
import Excel from "exceljs";
import { isArray } from "lodash";

const ExcelImportContext = createContext<any>({});

interface ExcelImportContextValue {
    insertMode?: string;
    transMode?: boolean;
    file?: any;
}

const ExcelImportContextProvider = (props: { children: any }) => {
    const [importValue, setImportValue] = useState<ExcelImportContextValue>({ insertMode: 'Auto', transMode: true });

    return (<ExcelImportContext.Provider value={{ importValue, setImportValue }}>
        {props.children}
    </ExcelImportContext.Provider>)
};
const stepHeight = 300;
export function StepUpload(props: { importParams: any, style: React.CSSProperties }) {

    const sGlobal = useContext(BefGlobalContext);
    const { importValue, setImportValue } = useContext(ExcelImportContext);

    const importModeDataSource = [
        { value: "Auto", label: "Auto" },
        { value: "Insert", label: "Insert Only" },
        { value: "Update", label: "Update Only" }
    ];

    const onInsertModeChange = (value: any) => {
        setImportValue({ ...importValue, insertMode: value });
    }

    const onTransModeChange = (value: boolean) => {
        setImportValue({ ...importValue, transMode: value });
    }

    const onSuccess = (file: any, value: any) => {
        setImportValue({ ...importValue, file: file });
    };

    const uploadFormatterHandler = (response: any, file: File) => {
        return {
            success: response.success,
            message: response.success ? "上传成功" : response.error,
            fileId: response.fileId
        };
    }

    return (
        <div style={{ height: stepHeight, overflow: 'auto', ...props.style }}>
            <div style={{ height: '100%', width: '100%', display: "flex", flexDirection: "column" }}>
                {props.importParams?.needSave !== false &&
                    <>
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <div style={{ fontSize: '14px' }}>InsertMode</div>
                            <div className="custom-form-control">
                                <Select defaultValue={importValue.insertMode} style={{ marginLeft: 8, marginRight: 8, width: 120 }}
                                    dataSource={importModeDataSource}
                                    onChange={onInsertModeChange}
                                />
                            </div>

                        </div>
                        <div style={{ marginTop: 8, display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <div style={{ fontSize: '14px' }}>TransMode</div>
                            <Switch
                                size="small"
                                defaultChecked={importValue.transMode}
                                onChange={onTransModeChange}
                                style={{ marginLeft: 8, verticalAlign: "middle" }}
                            />
                        </div>
                    </>
                }
                <div style={{ marginTop: 8, display: "flex", flexDirection: "row", alignItems: "center" }}>
                    <Upload.Dragger style={{ flex: 1 }}
                        limit={1}
                        listType="text"
                        action={sGlobal.config.host + "/upload"}
                        data={{ path: `temp/import/${sGlobal.currentAppId}/${sGlobal.session.userId}`, sessionId: sGlobal.session.id }} //这里使用temp/import开头，是为了方便整个import目录删除清理，所以不用appId开头
                        onSuccess={onSuccess} formatter={uploadFormatterHandler}
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">
                        <div className="next-upload-drag">
                            <p className="next-upload-drag-icon">
                                <Icon type="upload" />
                            </p>
                            <p className="next-upload-drag-text">
                                click to upload or drag file here
                            </p>
                            <p className="next-upload-drag-hint">supports xlsx</p>
                        </div>
                    </Upload.Dragger>
                </div>
            </div>
        </div>
    );
}

export function StepPreview(props: { importParams: any, style: React.CSSProperties, load: (datas: any[], columns: string[]) => void }) {

    const [dataSource, setDataSource] = useState<any[] | undefined>([]);
    const [columns, setColumns] = useState<string[]>([]);
    const { importValue } = useContext(ExcelImportContext);
    const befRpc = useContext(BefRpcContext);
    const messageService = useContext(BefMessageServiceContext);

    useEffect(() => {
        setColumns([]);
        setDataSource(undefined);
        //这里对文件在本地Excel解析并显示，只有当前在display:undefine的时候才需要加载
        if (!props.style.display) {
            if (importValue.file) loadExcel(importValue.file.originFileObj);
            else if (props.importParams.fileId) loadExcel(props.importParams.fileId);
        }
    }, [props.style.display]);

    async function loadExcel(file: any) {
        try {
            const workbook = new Excel.Workbook();
            //这个file的基类是一个Bolb
            if (typeof file === 'string') {
                await workbook.xlsx.readFile(file);
            } else {
                await workbook.xlsx.load(file);
            }
            const worksheet = workbook.worksheets[0];
            const rowCount = worksheet.rowCount;
            if (rowCount > 0) {
                const firstRow = worksheet.getRow(1);
                if (firstRow.cellCount > 0) {
                    let columnArray: string[] = [];
                    let cellIndex = 1;
                    for (cellIndex; cellIndex <= firstRow.cellCount; cellIndex += 1) {
                        columnArray.push(firstRow.getCell(cellIndex).text);
                    }
                    setColumns(columnArray);
                    const rowsData = worksheet.getRows(2, rowCount - 1)?.map(row => {
                        const rowValue = row.values;
                        const rowData: any = {};
                        for (let i = 0; i < columnArray.length; i++) {
                            //这里要用toString，是因为Table不识别日期类型
                            rowData[columnArray[i]] = rowValue[i + 1]?.toString();
                        }
                        return rowData;
                    });
                    setDataSource(rowsData || []);
                    props.load?.(rowsData || [], columnArray);
                }
            } else {
                props.load?.([], []);
            }
        } catch (error) {
            try {
                //目前在解析服务端生成的excel的时候遇到异常，这里改由服务端解析
                const rowsData = await befRpc.callRpcAsync<any[]>("", "Beflam.Framework.Data", "Beflam.Framework.Data.Commons.Excel", "GetData", { FilePath: importValue.file?.response?.fileId || props.importParams.fileId });
                let columnArray: string[] = [];
                if (rowsData && rowsData.length > 0) {
                    let firstItem = rowsData[0];
                    for (let propName in firstItem) {
                        columnArray.push(propName);
                    }
                    setColumns(columnArray);
                }
                setDataSource(rowsData || []);
                props.load?.(rowsData || [], columnArray);
            } catch (rpcError) {
                messageService.showMessage(messageService.handleErr(rpcError, "GetExcelData"))
            }
        }
    }

    return (
        <div style={{ height: stepHeight, overflow: 'auto', ...props.style }} >
            <Table style={{ maxHeight: stepHeight }} tableLayout="fixed" dataSource={dataSource}>
                {columns.map(col => {
                    return <Table.Column width={100} title={col} dataIndex={col} />
                })}
            </Table>
        </div>
    );
}

export function StepImport(props: {
    importParams: any,
    style: React.CSSProperties,
    onSuccess?: () => void
}) {

    const [result, setResult] = useState<any>({
        Current: 0,
        Count: 0,
        SucessCount: 0,
        FailCount: 0
    });
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [percent, setPercent] = useState(0);
    const [columns, setColumns] = useState<string[]>([]);
    const { importValue } = useContext(ExcelImportContext);

    //这里调用Rpc来进行上传
    async function importExcel() {
        setErrorMsg(null);
        setPercent(0);
        setResult({
            Current: 0,
            Count: 0,
            SucessCount: 0,
            FailCount: 0
        });

        if (props.importParams && props.importParams.dataAdapter && props.importParams.dataAdapter.importExcel) {
            const { dataAdapter, ...otherParams } = props.importParams;
            const uploadId = BefGuid.newNGuid();
            try {
                await dataAdapter.importExcel({
                    id: uploadId,
                    filePath: importValue.file?.response?.fileId || props.importParams.fileId,
                    insertMode: importValue.insertMode,
                    commitMode: importValue.transMode ? "Trans" : "IgnoreFail",
                    ...otherParams
                });
            }
            catch (error: any) {
                setErrorMsg(error.message);
            }
            var intervalId = setInterval(() => {
                try {
                    dataAdapter.importExcel({
                        id: uploadId,
                        action: 'Query'
                    }).then((result: any) => {
                        if (result) {
                            setResult(result);
                            //设置列，异常列在最后面
                            let keys = [];
                            if (result.Data && result.Data.length > 0) {
                                var firstData = result.Data[0];
                                if (firstData) {
                                    for (let key in firstData) {
                                        keys.push(key);
                                    }
                                }
                            }
                            setColumns(keys);
                            //防止除0错误
                            if (result.Count && result.Count > 0) setPercent((result.Current / result.Count) * 100);
                            else setPercent(0);
                            if (result.Code !== 0 || (result.Stage && (result.Stage === "Error" || result.Stage === "Imported"))) {
                                setErrorMsg(result.Message);
                                clearInterval(intervalId);
                                if (result.Stage === "Imported") props.onSuccess?.();
                            }
                        }
                    });
                }
                catch (error: any) {
                    setErrorMsg(error.message);
                }
            }, 1000);
        } else {
            setErrorMsg('no data adapter provided');
        }
    }

    return (
        <div style={{ height: stepHeight, overflow: 'auto', ...props.style }}>
            <div style={{ height: '100%', width: '100%', display: "flex", flexDirection: 'column' }}>
                <Progress percent={percent} textRender={() => ""} />

                <div style={{ display: "flex", flexDirection: "row", marginTop: 5, marginBottom: 5, justifyContent: "space-between", alignItems: "center" }}>
                    <Button onClick={importExcel} type="primary" >
                        Import
                    </Button>
                    <div>Stage: {result.Stage} {result.Current} / {result.Count} Success: {result.SucessCount} Fail: {result.FailCount}</div>
                </div>

                {errorMsg && <div>Error: {errorMsg}</div>}

                {result && result.Data && <div style={{ marginTop: 5, flex: 1 }}>
                    <Table style={{ maxHeight: stepHeight - 80 }} tableLayout="fixed" dataSource={result.Data}>
                        {columns.map(col => {
                            return <Table.Column width={100} title={col} dataIndex={col} />
                        })}
                    </Table>
                </div>}
            </div>
        </div>
    );
}

const StepUploadContainer = memo(StepUpload);
const StepPreviewContainer = memo(StepPreview);
const StepImportContainer = memo(StepImport);

export function ExcelImport(props: any) {
    const messageService = useContext(BefMessageServiceContext);

    const StepItem = Step.Item;
    const ButtonGroup = Button.Group;
    const [currentStep, setCurrentStep] = useState(0);
    const [previewData, setPreviewData] = useState({ isLoaded: false, datas: [] as any[], columns: [] as string[] })

    useEffect(() => {
        //如果当前界面进来的时候，有importParams.fileId，则直接跳到第二步
        if (props.importParams && props.importParams.fileId) setCurrentStep(1);
    }, []);

    const setCurrentStepEx = (currentStep: number) => {
        setPreviewData({ isLoaded: false, datas: [], columns: [] });
        setCurrentStep(currentStep);
    };

    function onClick(currentStep: number) {
        setCurrentStepEx(currentStep);
    }

    function next() {
        const s = currentStep + 1;
        setCurrentStepEx(s > 2 ? 2 : s);
    }

    function prev() {
        const s = currentStep - 1;
        setCurrentStepEx(s < 0 ? 0 : s);
    }

    const step1Style = {
        display: currentStep === 0 ? undefined : 'none'
    };
    const step2Style = {
        display: currentStep === 1 ? undefined : 'none'
    };
    const step3Style = {
        display: currentStep === 2 ? undefined : 'none'
    };

    const getImportDatas = (datas: any[], columns: string[]): Promise<any[]> => {
        return new Promise((resolve) => {
            if (datas == null || datas.length === 0) { resolve([]); return; }
            let _columns: any = null;
            let convertFields: any = {};
            if (props.importParams) {
                if (isArray(props.importParams.columns) && (props.importParams.columns as any[]).length > 0) {
                    _columns = {};
                    (props.importParams.columns as any[]).forEach((col) => {
                        _columns[col.header] = col.key;
                    });
                }
                if (props.importParams.convertFields) {
                    for (let field in props.importParams.convertFields) {
                        let item: any = {};
                        for (let key in props.importParams.convertFields[field]) {
                            let newKey = props.importParams.convertFields[field][key]?.toString();
                            item[newKey] = key;
                        }
                        convertFields[field] = item;
                    }
                }
            }

            const result: any[] = [];
            const chunkSize = 5000;
            let index = 0;

            function processChunk() {
                const end = Math.min(index + chunkSize, datas.length);
                for (; index < end; index++) {
                    const data = datas[index];
                    let item: any = {};
                    for (let ci = 0; ci < columns.length; ci++) {
                        const col = columns[ci];
                        let newCol = col;
                        let newValue = data[col];
                        if (_columns) newCol = _columns[col] ?? col;
                        if (convertFields[newCol] && newValue != null) {
                            let valStr = newValue.toString();
                            if (convertFields[newCol][valStr] !== undefined) newValue = convertFields[newCol][valStr];
                        }
                        item[newCol] = newValue;
                    }
                    result.push(item);
                }
                if (index < datas.length) {
                    setTimeout(processChunk, 0);
                } else {
                    resolve(result);
                }
            }

            processChunk();
        });
    };

    const [confirming, setConfirming] = useState(false);

    const confirm = async () => {
        setConfirming(true);
        try {
            const result = await getImportDatas(previewData.datas, previewData.columns);
            props.onClose?.(result);
        } finally {
            setConfirming(false);
        }
    };

    //这里可以使用 https://fusion.design/pc/component/step?themeid=2#controlled-container 
    return (
        <ExcelImportContextProvider>
            <div style={{ display: "flex", flexDirection: "column" }}>
                <Step current={currentStep} shape="circle" animation={true} >
                    <StepItem title="Step 1" onClick={onClick} />
                    <StepItem title="Step 2" onClick={onClick} />
                    {props.importParams?.needSave !== false && <StepItem title="Step 3" onClick={onClick} />}
                </Step>
                <div className="steps-content" style={{ padding: 20, flex: 1, width: "100%", height: '200px', minHeight: '200px' }}>
                    <StepUploadContainer importParams={props.importParams} style={step1Style} />
                    <StepPreviewContainer importParams={props.importParams} style={step2Style}
                        load={(datas: any[], columns: string[]) => {
                            setPreviewData({ isLoaded: true, datas, columns });
                        }}
                    />
                    <StepImportContainer importParams={props.importParams} style={step3Style}
                        onSuccess={async () => {
                            await messageService.showMessage({ mes: '导入成功' });
                            props.onClose?.(true);
                        }} />
                </div>
                <ButtonGroup>
                    <Button onClick={prev} type="primary" disabled={currentStep === 0} style={{ fontSize: '14px' }} >
                        Backward
                    </Button>
                    {props.importParams?.needSave === false && currentStep === 1 ?
                        <Button onClick={confirm} type="primary" loading={confirming} disabled={!previewData.isLoaded || confirming} style={{ fontSize: '14px' }} >
                            Confirm
                        </Button> :
                        <Button onClick={next} type="primary" disabled={currentStep === 2} style={{ fontSize: '14px' }} >
                            Forward
                        </Button>
                    }
                </ButtonGroup>
            </div>
        </ExcelImportContextProvider>
    );
}
